import { useLayoutEffect } from 'react'
import { Trans } from 'react-i18next'

import styled from 'styled-components'
import introArtPNG from '~/assets/images/art/art-intro.png'
import introArtWEBP from '~/assets/images/art/art-intro.webp'
import introColumnPNG from '~/assets/images/art/intro-column.png'
import introColumnWEBP from '~/assets/images/art/intro-column.webp'
import {
  ANATOMY_COMPACT,
  anatomyCompact,
} from '~/pages/anatomy/libs/responsive'
import { HistoryInfoPageStyles as S } from '~/pages/history/libs/infoPageStyles'
import { COLORS } from '~/shared/constants/uiConstants'
import { useNextStep } from '~/shared/hooks/useNextStep'
import { TPageProps } from '~/shared/types/commonTypes'

const HEADER_BG = '#eeeef9'
const ACCENT_COLOR = '#8c67f6'

const IntroArtRoot = styled(S.Root)`
  &::before {
    background-color: ${HEADER_BG};
  }
`

// Matches the shared "intro" typography scale used by intro-anatomy-1 / intro-person-1,
// which is bigger than the generic info-card Title/Subtitle this file otherwise reuses.
const IntroArtTitle = styled(S.Title)`
  font-size: 32px;
  line-height: 38px;
  text-align: center;

  span {
    color: ${ACCENT_COLOR};
    font-weight: inherit;
  }

  ${anatomyCompact} {
    font-size: ${ANATOMY_COMPACT.fontSize.f32};
    line-height: ${ANATOMY_COMPACT.lineHeight.lh38};
  }
`

const IntroArtSubtitle = styled(S.Subtitle)`
  font-size: 15px;
  font-weight: 400;
  line-height: 20px;
  text-align: center;
  color: ${COLORS.GRAY_700};

  ${anatomyCompact} {
    font-size: ${ANATOMY_COMPACT.fontSize.f15};
    line-height: ${ANATOMY_COMPACT.lineHeight.lh20};
  }
`

const ColumnImageWrapper = styled(S.ImageWrapper)`
  max-width: 112px;
  margin: 16px auto 0;
`

const IntroArt = ({ nextPagePath, pageId }: TPageProps) => {
  useLayoutEffect(() => {
    document.documentElement.style.setProperty('--header-bg', HEADER_BG)
    return () => {
      document.documentElement.style.removeProperty('--header-bg')
    }
  }, [])

  const handleContinue = useNextStep({
    nextPagePath,
    pageId,
    pageName: pageId,
    question: 'An intelligent person should know Art',
  })

  return (
    <IntroArtRoot>
      <S.StyledContainer>
        <S.ImageWrapper>
          <picture>
            <source srcSet={introArtWEBP} type="image/webp" />
            <img src={introArtPNG} alt="Art" />
          </picture>
        </S.ImageWrapper>

        <IntroArtTitle>
          <Trans
            ns="art"
            i18nKey="onboarding.introArt.title"
            components={{ span: <span /> }}
          />
        </IntroArtTitle>

        <IntroArtSubtitle>
          <Trans ns="art" i18nKey="onboarding.introArt.subtitle" />
        </IntroArtSubtitle>

        <ColumnImageWrapper>
          <picture>
            <source srcSet={introColumnWEBP} type="image/webp" />
            <img src={introColumnPNG} alt="Column" />
          </picture>
        </ColumnImageWrapper>

        <S.StyledButton type="button" onClick={() => handleContinue('')}>
          <Trans i18nKey="actions.continue" />
        </S.StyledButton>
      </S.StyledContainer>
    </IntroArtRoot>
  )
}

export default IntroArt
